import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { FontFamily } from "../../../../../constants/fonts";
import { useTheme } from "../../../../../theme/useTheme";
import { useToast } from "../../../../../components/LhToastContainer";
import TableSkeleton from "../../../components/TableSkeleton";
import { useLocationSummaryApi } from "../hooks/useLocationSummaryApi";
import {
  fetchLocationSummary,
  buildLocationSummaryPayload,
  LocationSummaryDataItem,
} from "../services/onTimeDispatchLocationSummaryApi";
import {
  normalizeFilterValue,
  toISODate,
} from "../OnTimeDispatchDashboard";
import LocationSummaryMobileCards from "./LocationSummaryMobileCards";
import type { AppliedFiltersSnapshot } from "../OnTimeDispatchDashboard";
//import { useTheme } from "../../../../../theme/useTheme";
import { buildOnTimeLocationSummaryWorkbook } from "../utils/onTimeDispatchLocationSummaryWorkbookBuilder";
import {
  aggregateLocationSummaryRowsByDimensions,
  buildLocationSummaryHierarchyRows,
  deriveLocationSummaryHierarchyMode,
  filterLocationSummaryHierarchyRows,
  hierarchySourceDimension,
} from "../utils/onTimeDispatchLocationSummaryHierarchy";
import { downloadWorkbook } from "../../../../AnalyticsHub/utils/workbookDownload";
// Max height of the scrollable data rows (Grand Total stays pinned above it)
const LOCATION_SUMMARY_BODY_MAX_HEIGHT = 560;



type LocationDetailDimensionKey =
  | "region"
  | "market"
  | "location"
  | "locationType"
  | "year"
  | "period"
  | "week"
  | "deliveryDate";

const LOCATION_DETAIL_DIMENSIONS: {
  key: LocationDetailDimensionKey;
  label: string;
}[] = [
  { key: "region", label: "Region / Unit" },
  { key: "market", label: "Market / POD" },
  { key: "location", label: "Location" },
  { key: "locationType", label: "Location Type" },
  { key: "year", label: "Year" },
  { key: "period", label: "Period" },
  { key: "week", label: "Week in Year" },
  { key: "deliveryDate", label: "Delivery Date" },
];

const DEFAULT_LOCATION_DETAIL_SLOTS: LocationDetailDimensionKey[] = ["region"];

interface LocationDetailSlot {
  id: number;
  selected: LocationDetailDimensionKey | null;
}

interface LocationSummaryRow {
  id?: string;
  isGrandTotal?: boolean;
  /** Rollup metadata (region/market/location rows) — same shape as ASN Gap's hierarchy rows. */
  groupType?: "region" | "market" | "location";
  parentRegionExpandKey?: string;
  parentMarketExpandKey?: string;
  dimensions: Record<LocationDetailDimensionKey, string>;
  /** Resolved location ID (when known) — used to drive navigation filter selections. */
  locationId?: string;
  planned: number;
  completed: number;
  incomplete: number;
  onTime: number;
  daeN: number;
  daeD: number;
  completedPct: number;
  onTimePct: number;
  daePct: number;
}

interface LocationTableColumnConfig {
  // Geography section
  region: boolean;
  marketPod: boolean;
  location: boolean;
  locationType: boolean;
  // Time section
  year: boolean;
  period: boolean;
  weekInYear: boolean;
  deliveryDate: boolean;
}

interface LocationTableColumnSectionState {
  geographyExpanded: boolean;
  timeframeExpanded: boolean;
}

export const DEFAULT_LOCATION_TABLE_COLUMN_CONFIG: LocationTableColumnConfig = {
  region: true,
  marketPod: false,
  location: false,
  locationType: false,
  year: false,
  period: false,
  weekInYear: false,
  deliveryDate: false,
};

export const DEFAULT_LOCATION_TABLE_COLUMN_SECTION_STATE: LocationTableColumnSectionState = {
  geographyExpanded: true,
  timeframeExpanded: true,
};

const LOCATION_TABLE_COLUMNS = [
  {
    key: "region" as const,
    label: "Region / Unit",
    dimensionKey: "region" as const,
    group: "Geography",
  },
  {
    key: "marketPod" as const,
    label: "Market / POD",
    dimensionKey: "market" as const,
    group: "Geography",
  },
  {
    key: "location" as const,
    label: "Location",
    dimensionKey: "location" as const,
    group: "Geography",
  },
  {
    key: "locationType" as const,
    label: "Location Type",
    dimensionKey: "locationType" as const,
    group: "Geography",
  },
  {
    key: "year" as const,
    label: "Year",
    dimensionKey: "year" as const,
    group: "Time",
  },
  {
    key: "period" as const,
    label: "Period",
    dimensionKey: "period" as const,
    group: "Time",
  },
  {
    key: "weekInYear" as const,
    label: "Week in Year",
    dimensionKey: "week" as const,
    group: "Time",
  },
  {
    key: "deliveryDate" as const,
    label: "Delivery Date",
    dimensionKey: "deliveryDate" as const,
    group: "Time",
  },
];

const fmtCount = (value: number): string => value.toLocaleString("en-US");

type LocSummarySortKey =
  | LocationDetailDimensionKey
  | "planned"
  | "completed"
  | "completedPct"
  | "incomplete"
  | "onTime"
  | "onTimePct"
  | "daeN"
  | "daePct";

interface LocationSummaryTabProps {
  appliedFilters: AppliedFiltersSnapshot;
  adminLocations: any[];
  locationTableColumnConfig: LocationTableColumnConfig;
  setLocationTableColumnConfig: React.Dispatch<React.SetStateAction<LocationTableColumnConfig>>;
  locationTableColumnSectionState: LocationTableColumnSectionState;
  setLocationTableColumnSectionState: React.Dispatch<React.SetStateAction<LocationTableColumnSectionState>>;
  /** When true, applies iPad dark mode styling (tablet iOS in dark theme). */
  isDarkTablet?: boolean;
  /**
   * Called when a Region/Unit, Market/POD, or Location cell is pressed.
   * Caller is expected to switch to Executive Overview and apply the
   * corresponding filter while preserving all other applied filters.
   */
  onNavigateToExecutiveOverview?: (
    filterType: "region" | "market" | "location",
    item: { id: string; displayText: string }
  ) => void;
  /**
   * Called when an Incomplete Loads value (> 0) is pressed. Caller is
   * expected to switch to Load Execution with Status set to Incomplete.
   * When `location` is provided (row is at Location grain), the caller
   * should also apply that Location filter.
   */
  onNavigateToLoadExecutionIncomplete?: (
    location?: { id: string; displayText: string }
  ) => void;
}

interface LocationSummaryDimensionFallbacks {
  selectedYearLabel: string;
  selectedPeriodLabel: string;
  selectedWeekLabel: string;
  selectedDeliveryDate: string;
}

// Shared by the on-screen table (current page) and full-dataset export so both stay in sync.
const mapLocationSummaryRecord = (
  record: LocationSummaryDataItem,
  adminLocations: any[],
  fallbacks: LocationSummaryDimensionFallbacks
): LocationSummaryRow => {
  const locationId = record.grainKey?.locationId || "";
  const locationNameFromApi = record.grainKey?.locationName || "";
  const locationTypeFromApi = record.grainKey?.locType || record.grainKey?.locationType || "";

  let locationName = locationNameFromApi;
  let matchingLocation: any = null;

  if (!locationName && locationId) {
    matchingLocation = adminLocations.find(
      (loc) =>
        String(loc.locationId ?? loc.locId).trim() === String(locationId).trim()
    );
    locationName = matchingLocation?.locName || matchingLocation?.name || locationId;
  }

  if (!locationName) {
    locationName = "Unknown Location";
  }

  if (!matchingLocation && locationId) {
    matchingLocation = adminLocations.find(
      (loc) =>
        String(loc.locationId ?? loc.locId).trim() === String(locationId).trim()
    );
  }

  let resolvedLocationType = locationTypeFromApi;

  if (locationTypeFromApi) {
    const locationTypeAsId = adminLocations.find(
      (loc) =>
        String(loc.locationId ?? loc.locId).trim() ===
        String(locationTypeFromApi).trim()
    );
    if (locationTypeAsId) {
      resolvedLocationType = locationTypeAsId.locationFacilityType || locationTypeFromApi;
    } else if (matchingLocation) {
      resolvedLocationType = matchingLocation.locationFacilityType || locationTypeFromApi;
    }
  }

  const dimensionValues: Record<LocationDetailDimensionKey, string> = {
    region: record.grainKey?.region || "Unknown Region",
    market: record.grainKey?.market || "Unknown Market / POD",
    location: locationName,
    locationType: resolvedLocationType,
    year: record.grainKey?.year || fallbacks.selectedYearLabel,
    period: record.grainKey?.period || fallbacks.selectedPeriodLabel,
    week: record.grainKey?.week || fallbacks.selectedWeekLabel,
    deliveryDate: record.grainKey?.deliveryDate || fallbacks.selectedDeliveryDate,
  };

  const planned = record.planned || 0;
  const completed = record.completed || 0;
  const incomplete = record.incomplete || 0;
  const onTime = record.onTime || 0;
  const daeN = record.driverArrivedAsExpected || 0;
  const daeD = record.driverManifestCount || 0;

  // Always compute from raw counts — the API's pre-rounded percentage fields
  // (completedPercentage, onTimePercentage, driverArrivedPercentage) are
  // integer-rounded and cause mismatches with the Excel export, which derives
  // percentages from the same raw counts. Using the raw counts here guarantees
  // both UI and Excel show identical values (e.g. 97.72% not 98.00%).
  const completedPct = planned ? (completed / planned) * 100 : 0;
  const onTimePct = planned ? (onTime / planned) * 100 : 0;
  const daePct = daeD ? (daeN / daeD) * 100 : 0;
  const rowId = JSON.stringify(record.grainKey || {});

  return {
    id: rowId,
    isGrandTotal: false,
    dimensions: dimensionValues,
    locationId: locationId || undefined,
    planned,
    completed,
    incomplete,
    onTime,
    daeN,
    daeD,
    completedPct,
    onTimePct,
    daePct,
  };
};

const createLocationSummaryGrandTotalRow = (
  totals: LocationSummaryDataItem
): LocationSummaryRow => {
  const planned = totals.planned || 0;
  const completed = totals.completed || 0;
  const incomplete = totals.incomplete || 0;
  const onTime = totals.onTime || 0;
  const daeN = totals.driverArrivedAsExpected || 0;
  const daeD = totals.driverManifestCount || 0;

  return {
    id: 'grand-total',
    isGrandTotal: true,
    dimensions: {
      region: 'Grand Total',
      market: 'Grand Total',
      location: 'Grand Total',
      locationType: 'Grand Total',
      year: 'Grand Total',
      period: 'Grand Total',
      week: 'Grand Total',
      deliveryDate: 'Grand Total',
    },
    planned,
    completed,
    incomplete,
    onTime,
    daeN,
    daeD,
    // Always compute from raw counts for the same reason as mapLocationSummaryRecord.
    completedPct: planned ? (completed / planned) * 100 : 0,
    onTimePct: planned ? (onTime / planned) * 100 : 0,
    daePct: daeD ? (daeN / daeD) * 100 : 0,
  };
};

const computeLocationSummaryTotalRow = (rows: LocationSummaryRow[]): LocationSummaryRow => {
  const total = rows.reduce(
    (acc, row) => {
      acc.planned += row.planned;
      acc.completed += row.completed;
      acc.incomplete += row.incomplete;
      acc.onTime += row.onTime;
      acc.daeN += row.daeN;
      acc.daeD += row.daeD;
      return acc;
    },
    { planned: 0, completed: 0, incomplete: 0, onTime: 0, daeN: 0, daeD: 0 }
  );

  return {
    dimensions: {
      region: "Grand Total",
      market: "Grand Total",
      location: "Grand Total",
      locationType: "Grand Total",
      year: "Grand Total",
      period: "Grand Total",
      week: "Grand Total",
      deliveryDate: "Grand Total",
    },
    planned: total.planned,
    completed: total.completed,
    incomplete: total.incomplete,
    onTime: total.onTime,
    daeN: total.daeN,
    daeD: total.daeD,
    completedPct: total.planned ? (total.completed / total.planned) * 100 : 0,
    onTimePct: total.planned ? (total.onTime / total.planned) * 100 : 0,
    daePct: total.daeD ? (total.daeN / total.daeD) * 100 : 0,
  };
};

const compareLocationSummaryRows = (
  sortKey: LocSummarySortKey,
  sortDir: "asc" | "desc"
) => {
  const dir = sortDir === "asc" ? 1 : -1;
  const metricKeys: string[] = [
    "planned",
    "completed",
    "completedPct",
    "incomplete",
    "onTime",
    "onTimePct",
    "daeN",
    "daePct",
  ];
  return (a: LocationSummaryRow, b: LocationSummaryRow): number => {
    if (metricKeys.includes(sortKey)) {
      const aVal = a[sortKey as keyof LocationSummaryRow] as number;
      const bVal = b[sortKey as keyof LocationSummaryRow] as number;
      return dir * (aVal - bVal);
    }
    const aVal = a.dimensions[sortKey as LocationDetailDimensionKey] ?? "";
    const bVal = b.dimensions[sortKey as LocationDetailDimensionKey] ?? "";
    return dir * aVal.localeCompare(bVal);
  };
};

const sortLocationSummaryRows = (
  rows: LocationSummaryRow[],
  sortKey: LocSummarySortKey | null,
  sortDir: "asc" | "desc"
): LocationSummaryRow[] => {
  if (!sortKey) return rows;
  return [...rows].sort(compareLocationSummaryRows(sortKey, sortDir));
};

const LocationSummaryTab: React.FC<LocationSummaryTabProps> = ({
  appliedFilters,
  adminLocations,
  locationTableColumnConfig,
  setLocationTableColumnConfig,
  locationTableColumnSectionState,
  setLocationTableColumnSectionState,
  isDarkTablet = false,
  onNavigateToExecutiveOverview,
  onNavigateToLoadExecutionIncomplete,
}) => {
  const { width, height: windowHeight } = useWindowDimensions();
  const { resolvedTheme } = useTheme();
  // ✅ Mobile detection: iOS/native with width < 768 = mobile phone, not tablet
  const isMobilePhone = Platform.OS === 'ios' && width < 768;
  const isDarkPhone = isMobilePhone && resolvedTheme === 'dark';
  // isDark covers both mobile phone dark AND tablet iPad dark
  const isDark = isDarkPhone || isDarkTablet;
  const { colors } = useTheme();
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const [locationDetailsDraftSlots, setLocationDetailsDraftSlots] = useState<
    LocationDetailSlot[]
  >(
    DEFAULT_LOCATION_DETAIL_SLOTS.map((key, index) => ({
      id: index + 1,
      selected: key,
    }))
  );
  const [locationDetailsAppliedSlots, setLocationDetailsAppliedSlots] =
    useState<LocationDetailSlot[]>(
      DEFAULT_LOCATION_DETAIL_SLOTS.map((key, index) => ({
        id: index + 1,
        selected: key,
      }))
    );
  const [openLocationDetailSlotId, setOpenLocationDetailSlotId] = useState<
    number | null
  >(null);

  const [isLocationColumnsPanelOpen, setIsLocationColumnsPanelOpen] =
    useState(false);
  const [locationBtnLayout, setLocationBtnLayout] = useState({ top: 0 });
  const locationColumnsBtnRef = useRef<any>(null);
  const locationCardRef = useRef<any>(null);

  // Column picker edits are staged in a draft and only committed on Apply.
  const [draftLocationColumnConfig, setDraftLocationColumnConfig] =
    useState<LocationTableColumnConfig>(locationTableColumnConfig);

  // Horizontal-scroll sync across the header, Grand Total, and body sections. Each is rendered as
  // its own ScrollView so the header + Grand Total can remain fixed in place while only the body
  // scrolls vertically (see tableBodyMaxHeight below); a guard ref prevents the onScroll handlers
  // from re-triggering each other in an infinite loop.
  const headerScrollRef = useRef<ScrollView>(null);
  const bodyScrollRef = useRef<ScrollView>(null);
  const grandTotalScrollRef = useRef<ScrollView>(null);
  const isSyncingTableScrollRef = useRef(false);

  const handleTableHorizontalScroll = useCallback(
    (source: "header" | "body", x: number) => {
      if (isSyncingTableScrollRef.current) return;
      isSyncingTableScrollRef.current = true;
      if (source !== "header") headerScrollRef.current?.scrollTo({ x, animated: false });
      if (source !== "body") bodyScrollRef.current?.scrollTo({ x, animated: false });
      grandTotalScrollRef.current?.scrollTo({ x, animated: false });
      requestAnimationFrame(() => {
        isSyncingTableScrollRef.current = false;
      });
    },
    []
  );

  // Caps the table body's height to whatever vertical space remains below it in the viewport
  // (viewport height minus the body's current top offset, the footer/pagination row's measured
  // height, and a small safety margin) so the table never grows taller than the available screen
  // — the body then scrolls internally while the header and Grand Total row (rendered outside this
  // capped container, see below) stay fixed in place. Recomputed on layout/resize rather than
  // hardcoded so it adapts to different screen sizes and to whatever sits above the table.
  const tableBodyAnchorRef = useRef<View>(null);
  const [tableBodyMaxHeight, setTableBodyMaxHeight] = useState<number | null>(null);
  const [tableFooterHeight, setTableFooterHeight] = useState(64);

  const recomputeTableBodyHeight = useCallback(() => {
    const anchorNode = tableBodyAnchorRef.current as any;
    if (!anchorNode || typeof anchorNode.measureInWindow !== "function") return;
    anchorNode.measureInWindow((_x: number, y: number) => {
      const MIN_TABLE_BODY_HEIGHT = 220;
      const BOTTOM_SAFE_MARGIN = 24;
      const available = windowHeight - y - tableFooterHeight - BOTTOM_SAFE_MARGIN;
      setTableBodyMaxHeight(Math.max(MIN_TABLE_BODY_HEIGHT, Math.floor(available)));
    });
  }, [windowHeight, tableFooterHeight]);

  const openLocationColumnsDropdown = () => {
    setDraftLocationColumnConfig(locationTableColumnConfig);
    const btnNode = locationColumnsBtnRef.current;
    const cardNode = locationCardRef.current;
    if (btnNode && cardNode && typeof btnNode.measureLayout === "function") {
      btnNode.measureLayout(
        cardNode,
        (_x: number, y: number, _w: number, h: number) => {
          setLocationBtnLayout({ top: y + h + 6 });
          setIsLocationColumnsPanelOpen(true);
        },
        () => {
          setIsLocationColumnsPanelOpen(true);
        }
      );
    } else {
      setIsLocationColumnsPanelOpen(true);
    }
  };

  const [locSummarySortKey, setLocSummarySortKey] =
    useState<LocSummarySortKey | null>(null);
  const [locSummarySortDir, setLocSummarySortDir] = useState<"asc" | "desc">(
    "asc"
  );
  const [locSummaryPageSize, setLocSummaryPageSize] = useState<25 | 50 | 100 | 150 | 200>(200);
  const [isLocSummaryExporting, setIsLocSummaryExporting] = useState(false);
  // Expanded region/market rollup rows (ASN Gap parity). Collapsed by default.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const handleToggleGroup = useCallback((groupId?: string) => {
    if (!groupId) return;
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);
  const { showToast } = useToast();

  // Compute grain columns (dimensions) for Location Summary API - DYNAMIC based on applied filters
  const locationSummaryGrainColumns = useMemo(() => {
    const grainColumns: LocationDetailDimensionKey[] = [];

    if (appliedFilters.selectedLocations.length === 0) {
      grainColumns.push("region");
      grainColumns.push("market");
      grainColumns.push("location");
      grainColumns.push("locationType");
    }

    if (appliedFilters.selectedYears.length === 0) {
      grainColumns.push("year");
    }
    if (appliedFilters.selectedPeriods.length === 0) {
      grainColumns.push("period");
    }
    if (appliedFilters.selectedWeeks.length === 0) {
      grainColumns.push("week");
    }

    if (!appliedFilters.deliveryDateRange.start && !appliedFilters.deliveryDateRange.end) {
      grainColumns.push("deliveryDate");
    }

    if (grainColumns.length === 0) {
      grainColumns.push("region");
      grainColumns.push("year");
    }

    return grainColumns;
  }, [appliedFilters]);

  // Reset to page 1 whenever filters, grouping, sort, or page size change.
  const [locSummaryPage, setLocSummaryPage] = useState<number>(1);

  // ✅ FIX: Properly convert AppliedFiltersSnapshot to AppliedFiltersForLocationSummary
  // Extract only the fiscal/geographic fields relevant to Location Summary API
  const locationSummaryFilters = useMemo(() => ({
    selectedRegions: appliedFilters.selectedRegions,
    selectedMarkets: appliedFilters.selectedMarkets,
    selectedLocations: appliedFilters.selectedLocations,
    selectedLocationTypes: appliedFilters.selectedLocationTypes,
    selectedYears: appliedFilters.selectedYears,
    selectedPeriods: appliedFilters.selectedPeriods,
    selectedWeeks: appliedFilters.selectedWeeks,
    deliveryDateRange: appliedFilters.deliveryDateRange,
  }), [appliedFilters]);

  // Reset page to 1 when filters/grouping change — done during render (not in a useEffect)
  // so it lands in the same commit as the filter change, avoiding a second wasted API call.
  const locationSummaryFiltersKey = useMemo(
    () => JSON.stringify({ locationSummaryFilters, locationSummaryGrainColumns }),
    [locationSummaryFilters, locationSummaryGrainColumns]
  );
  const prevLocationSummaryFiltersKeyRef = useRef(locationSummaryFiltersKey);
  if (prevLocationSummaryFiltersKeyRef.current !== locationSummaryFiltersKey) {
    prevLocationSummaryFiltersKeyRef.current = locationSummaryFiltersKey;
    if (locSummaryPage !== 1) {
      setLocSummaryPage(1);
    }
  }

  const {
    data: locationSummaryApiData,
    totals: locationSummaryTotals,
    loading: locationSummaryLoading,
    error: locationSummaryError,
    totalElements: locationSummaryTotalElements,
    totalPages: locationSummaryTotalPages,
  } = useLocationSummaryApi(
    locationSummaryFilters,
    true,
    adminLocations,
    locSummaryPage, // page (1-based, not 0-based)
    locSummaryPageSize, // size
    locationSummaryGrainColumns, // grainColumns
    locSummarySortKey, // sort key
    locSummarySortDir // sort direction
  );

  const handleLocSummaryPageChange = useCallback((newPage: number) => {
    setLocSummaryPage(newPage);
  }, []);

  const handleLocSummaryPageSizeChange = useCallback((size: 25 | 50 | 100 | 150 | 200) => {
    setLocSummaryPageSize(size);
    setLocSummaryPage(1);
  }, []);

  const locationDetailHasPendingChanges = useMemo(
    () =>
      JSON.stringify(locationDetailsDraftSlots) !==
      JSON.stringify(locationDetailsAppliedSlots),
    [locationDetailsAppliedSlots, locationDetailsDraftSlots]
  );

  const canAddLocationDetailColumn =
    locationDetailsDraftSlots.length < LOCATION_DETAIL_DIMENSIONS.length;

  const canApplyLocationDetailConfig = useMemo(() => {
    const hasUnselectedDynamicSlot = locationDetailsDraftSlots.some(
      (slot, index) => index > 0 && !slot.selected
    );
    if (hasUnselectedDynamicSlot) return false;
    return locationDetailHasPendingChanges;
  }, [locationDetailHasPendingChanges, locationDetailsDraftSlots]);

  const locationDetailDimensionLabelMap = useMemo(() => {
    return LOCATION_DETAIL_DIMENSIONS.reduce<
      Record<LocationDetailDimensionKey, string>
    >(
      (acc, item) => {
        acc[item.key] = item.label;
        return acc;
      },
      {
        region: "Region / Unit",
        market: "Market / POD",
        location: "Location",
        locationType: "Location Type",
        year: "Year",
        period: "Period",
        week: "Week in Year",
        deliveryDate: "Delivery Date",
      }
    );
  }, []);

  const getUsedDimensionKeysExcludingSlot = useCallback(
    (slotId: number) => {
      return new Set(
        locationDetailsDraftSlots
          .filter((slot) => slot.id !== slotId)
          .map((slot) => slot.selected)
          .filter((key): key is LocationDetailDimensionKey => Boolean(key))
      );
    },
    [locationDetailsDraftSlots]
  );

  const isLocationDetailOptionDisabled = useCallback(
    (slotId: number, optionKey: LocationDetailDimensionKey) => {
      const slotIndex = locationDetailsDraftSlots.findIndex(
        (slot) => slot.id === slotId
      );
      const isNonPrimarySlot = slotIndex > 0;
      if (isNonPrimarySlot && optionKey === "region") {
        return true;
      }

      const usedElsewhere = getUsedDimensionKeysExcludingSlot(slotId);
      return usedElsewhere.has(optionKey);
    },
    [getUsedDimensionKeysExcludingSlot, locationDetailsDraftSlots]
  );

  const handleAddLocationDetailColumn = useCallback(() => {
    if (!canAddLocationDetailColumn) return;
    const nextSlotId = locationDetailsDraftSlots.length + 1;
    setLocationDetailsDraftSlots((prev) => [
      ...prev,
      { id: prev.length + 1, selected: null },
    ]);
    setOpenLocationDetailSlotId(nextSlotId);
  }, [canAddLocationDetailColumn, locationDetailsDraftSlots.length]);

  const handleLocationDetailSelection = useCallback(
    (slotId: number, key: LocationDetailDimensionKey) => {
      setLocationDetailsDraftSlots((prev) =>
        prev.map((slot) =>
          slot.id === slotId ? { ...slot, selected: key } : slot
        )
      );
      setOpenLocationDetailSlotId(null);
    },
    []
  );

  const handleRemoveLocationDetailColumn = useCallback((slotId: number) => {
    setLocationDetailsDraftSlots((prev) => {
      const next = prev.filter((slot) => slot.id !== slotId);
      return next.map((slot, index) => ({ ...slot, id: index + 1 }));
    });
    setOpenLocationDetailSlotId((prev) => (prev === slotId ? null : prev));
  }, []);

  const handleApplyLocationDetailConfig = useCallback(() => {
    if (!canApplyLocationDetailConfig) return;
    setLocationDetailsAppliedSlots(locationDetailsDraftSlots);
    setOpenLocationDetailSlotId(null);
  }, [canApplyLocationDetailConfig, locationDetailsDraftSlots]);

  const handleResetLocationDetailConfig = useCallback(() => {
    const resetSlots = DEFAULT_LOCATION_DETAIL_SLOTS.map((key, index) => ({
      id: index + 1,
      selected: key,
    }));
    setLocationDetailsDraftSlots(resetSlots);
    setLocationDetailsAppliedSlots(resetSlots);
    setOpenLocationDetailSlotId(null);
    setLocationTableColumnConfig(DEFAULT_LOCATION_TABLE_COLUMN_CONFIG);
    setLocationTableColumnSectionState(DEFAULT_LOCATION_TABLE_COLUMN_SECTION_STATE);
  }, []);

  const handleLocationColumnToggle = useCallback(
    (columnKey: keyof LocationTableColumnConfig) => {
      setDraftLocationColumnConfig((prev) => ({
        ...prev,
        [columnKey]: !prev[columnKey],
      }));
    },
    []
  );

  const handleApplyLocationColumns = useCallback(() => {
    setLocationTableColumnConfig(draftLocationColumnConfig);
    setIsLocationColumnsPanelOpen(false);
  }, [draftLocationColumnConfig, setLocationTableColumnConfig]);

  const handleLocationColumnSelectAll = useCallback(() => {
    setDraftLocationColumnConfig({
      region: true,
      marketPod: true,
      location: true,
      locationType: true,
      year: true,
      period: true,
      weekInYear: true,
      deliveryDate: true,
    });
  }, []);

  const handleLocationColumnDeselectAll = useCallback(() => {
    setDraftLocationColumnConfig({
      region: true,
      marketPod: false,
      location: false,
      locationType: false,
      year: false,
      period: false,
      weekInYear: false,
      deliveryDate: false,
    });
  }, []);

  const handleLocationColumnExpandAll = useCallback(() => {
    setLocationTableColumnSectionState({
      geographyExpanded: true,
      timeframeExpanded: true,
    });
  }, []);

  const handleLocationColumnCollapseAll = useCallback(() => {
    setLocationTableColumnSectionState({
      geographyExpanded: false,
      timeframeExpanded: false,
    });
  }, []);

  const handleLocationColumnSectionToggle = useCallback(
    (section: keyof LocationTableColumnSectionState) => {
      setLocationTableColumnSectionState((prev) => ({
        ...prev,
        [section]: !prev[section],
      }));
    },
    []
  );

  const locationSummaryRows = useMemo<LocationSummaryRow[]>(() => {
    // No synthetic/mock rows: an empty API result means no data for the
    // applied filters, and the table should show its empty state instead.
    if (!locationSummaryApiData || locationSummaryApiData.length === 0) {
      return [];
    }

    const selectedYearLabel =
      appliedFilters.selectedYears[0]?.displayText || String(currentYear);
    const selectedPeriodLabel =
      appliedFilters.selectedPeriods[0]?.displayText || "P6";
    const selectedWeekLabel =
      appliedFilters.selectedWeeks[0]?.displayText?.split(" ")[0] || "W2";
    const selectedDeliveryDate =
      appliedFilters.deliveryDateRange.end ||
      appliedFilters.deliveryDateRange.start ||
      toISODate(new Date());

    return locationSummaryApiData.map((record) =>
      mapLocationSummaryRecord(record, adminLocations, {
        selectedYearLabel,
        selectedPeriodLabel,
        selectedWeekLabel,
        selectedDeliveryDate,
      })
    );
  }, [
    adminLocations,
    appliedFilters,
    currentYear,
    locationSummaryApiData,
  ]);

  const locationSummaryTotalRow = useMemo<LocationSummaryRow>(
    () => computeLocationSummaryTotalRow(locationSummaryRows),
    [locationSummaryRows]
  );

  // Prefer the API's totals (covers all pages). When the response has none, fall back
  // to summing the rows we received so the Grand Total row is still shown.
  const locationSummaryGrandTotalRow = useMemo<LocationSummaryRow | null>(() => {
    if (locationSummaryTotals) {
      return createLocationSummaryGrandTotalRow(locationSummaryTotals);
    }
    if (locationSummaryRows.length > 0) {
      return { ...locationSummaryTotalRow, id: "grand-total", isGrandTotal: true };
    }
    return null;
  }, [locationSummaryTotals, locationSummaryRows, locationSummaryTotalRow]);

  const appliedLocationDetailDimensions = useMemo(() => {
    return LOCATION_TABLE_COLUMNS
      .filter((col) => locationTableColumnConfig[col.key as keyof LocationTableColumnConfig])
      .map((col) => col.dimensionKey)
      .filter((key): key is LocationDetailDimensionKey => Boolean(key));
  }, [locationTableColumnConfig]);

  // ASN Gap parity: Region only -> Region > Market > Location rollups;
  // Region + Market -> Market > Location rollups; anything else stays flat.
  const locationSummaryHierarchyMode = useMemo(
    () => deriveLocationSummaryHierarchyMode(appliedLocationDetailDimensions),
    [appliedLocationDetailDimensions]
  );

  // The API returns rows at location x year x period x week level regardless of the
  // visible columns. In flat mode, merge rows that are identical on the visible
  // columns so each displayed row is unique (hierarchy modes collapse by location).
  const aggregatedLocationSummaryRows = useMemo<LocationSummaryRow[]>(
    () =>
      locationSummaryHierarchyMode === "flat"
        ? aggregateLocationSummaryRowsByDimensions(
            locationSummaryRows,
            appliedLocationDetailDimensions
          )
        : locationSummaryRows,
    [locationSummaryRows, locationSummaryHierarchyMode, appliedLocationDetailDimensions]
  );

  const sortedLocationSummaryRows = useMemo<LocationSummaryRow[]>(
    () => sortLocationSummaryRows(aggregatedLocationSummaryRows, locSummarySortKey, locSummarySortDir),
    [aggregatedLocationSummaryRows, locSummarySortKey, locSummarySortDir]
  );

  const pagedLocationSummaryRows = useMemo<LocationSummaryRow[]>(
    () => sortedLocationSummaryRows.slice(0, locSummaryPageSize),
    [sortedLocationSummaryRows, locSummaryPageSize]
  );

  // Mock-data fallback (no API content) has no real backend pagination — degrade to a single page.
  const isUsingApiLocationData =
    !!locationSummaryApiData && locationSummaryApiData.length > 0;
  const locationSummaryPaginationTotalElements = isUsingApiLocationData
    ? locationSummaryTotalElements
    : sortedLocationSummaryRows.length;
  const locationSummaryPaginationTotalPages = isUsingApiLocationData
    ? Math.max(locationSummaryTotalPages, 1)
    : 1;

  const locationSummaryHierarchyRows = useMemo<LocationSummaryRow[]>(
    () =>
      buildLocationSummaryHierarchyRows(
        pagedLocationSummaryRows,
        locationSummaryHierarchyMode,
        locSummarySortKey
          ? compareLocationSummaryRows(locSummarySortKey, locSummarySortDir)
          : undefined
      ),
    [
      pagedLocationSummaryRows,
      locationSummaryHierarchyMode,
      locSummarySortKey,
      locSummarySortDir,
    ]
  );

  // Rows actually rendered in the body (collapsed rollups hide their children).
  const locationSummaryBodyRows = useMemo<LocationSummaryRow[]>(
    () =>
      filterLocationSummaryHierarchyRows(
        locationSummaryHierarchyRows,
        locationSummaryHierarchyMode,
        expandedGroups
      ),
    [locationSummaryHierarchyRows, locationSummaryHierarchyMode, expandedGroups]
  );

  // Build subtitle that shows the active grain hierarchy
  const locationSummaryTableSubtitle = useMemo(() => {
    if (appliedLocationDetailDimensions.length === 0) return '';
    
    // Map dimension keys to their labels and build hierarchy string
    const hierarchyParts = appliedLocationDetailDimensions
      .map((dim) => locationDetailDimensionLabelMap[dim])
      .filter(Boolean);
    
    return hierarchyParts.length > 0 ? hierarchyParts.join(' > ') + ' level' : '';
  }, [appliedLocationDetailDimensions, locationDetailDimensionLabelMap]);

  // Region/Market/Location cell press → Executive Overview with that single
  // entity selected as the corresponding filter, preserving all other filters
  // (the parent dashboard owns appliedFilters and is responsible for the merge).
  const handleLocationSummaryEntityPress = useCallback(
    (
      dimensionKey: "region" | "market" | "location",
      row: LocationSummaryRow
    ) => {
      if (!onNavigateToExecutiveOverview) return;
      const displayText = row.dimensions[dimensionKey];
      if (!displayText || displayText === "Grand Total") return;
      const id =
        dimensionKey === "market"
          ? normalizeFilterValue(displayText)
          : dimensionKey === "location"
          ? row.locationId || displayText
          : displayText;
      onNavigateToExecutiveOverview(dimensionKey, { id, displayText });
    },
    [onNavigateToExecutiveOverview]
  );

  // Incomplete Loads cell press (only rendered as a link when count > 0) →
  // Load Execution with Status = Incomplete. When the table is currently
  // grouped at Location grain, also select that row's Location so the
  // Load Execution + scorecards requests are scoped to it.
  //
  // Restricted to Desktop (web) only — on native mobile and iPad this must be
  // a complete no-op (no tab switch, no filter/state change, no API calls).
  const handleLocationSummaryIncompletePress = useCallback(
    (row: LocationSummaryRow) => {
      if (Platform.OS !== 'web') return;
      if (!onNavigateToLoadExecutionIncomplete || row.incomplete <= 0) return;
      const isLocationGrain = appliedLocationDetailDimensions.includes("location");
      if (isLocationGrain && row.dimensions.location) {
        onNavigateToLoadExecutionIncomplete({
          id: row.locationId || row.dimensions.location,
          displayText: row.dimensions.location,
        });
      } else {
        onNavigateToLoadExecutionIncomplete(undefined);
      }
    },
    [onNavigateToLoadExecutionIncomplete, appliedLocationDetailDimensions]
  );

  const handleLocSummarySort = useCallback(
    (key: LocSummarySortKey) => {
      if (locSummarySortKey !== key) {
        setLocSummarySortKey(key);
        setLocSummarySortDir("asc");
        setLocSummaryPage(1);
        return;
      }
      if (locSummarySortDir === "asc") {
        setLocSummarySortDir("desc");
        setLocSummaryPage(1);
        return;
      }
      setLocSummarySortKey(null);
      setLocSummarySortDir("asc");
      setLocSummaryPage(1);
    },
    [locSummarySortKey, locSummarySortDir]
  );

  const handleLocSummaryExport = useCallback(async () => {
    if (typeof document === "undefined" || isLocSummaryExporting) return;
    setIsLocSummaryExporting(true);
    try {
      const EXPORT_PAGE_SIZE = 1000;
      const payload = buildLocationSummaryPayload(
        locationSummaryFilters,
        adminLocations,
        1,
        EXPORT_PAGE_SIZE,
        locationSummaryGrainColumns,
        locSummarySortKey ?? undefined,
        locSummarySortDir
      );

      const first = await fetchLocationSummary(payload);
      const allRecords: LocationSummaryDataItem[] = [...(first.data.content ?? [])];
      const totalPages = first.data.totalPages ?? 1;
      for (let page = 2; page <= totalPages; page++) {
        const next = await fetchLocationSummary({ ...payload, page });
        allRecords.push(...(next.data.content ?? []));
      }

      const selectedYearLabel =
        appliedFilters.selectedYears[0]?.displayText || String(currentYear);
      const selectedPeriodLabel =
        appliedFilters.selectedPeriods[0]?.displayText || "P6";
      const selectedWeekLabel =
        appliedFilters.selectedWeeks[0]?.displayText?.split(" ")[0] || "W2";
      const selectedDeliveryDate =
        appliedFilters.deliveryDateRange.end ||
        appliedFilters.deliveryDateRange.start ||
        toISODate(new Date());

      const exportedRows = allRecords.map((record) =>
        mapLocationSummaryRecord(record, adminLocations, {
          selectedYearLabel,
          selectedPeriodLabel,
          selectedWeekLabel,
          selectedDeliveryDate,
        })
      );
      const sortedExportedRows = sortLocationSummaryRows(
        aggregateLocationSummaryRowsByDimensions(
          exportedRows,
          appliedLocationDetailDimensions
        ),
        locSummarySortKey,
        locSummarySortDir
      );
      // Use the API's pre-computed totals for the grand total row — the same
      // source the UI uses via createLocationSummaryGrandTotalRow — so that
      // percentage values (onTimePercentage, daePct, etc.) are identical to
      // what is displayed on screen. Re-computing from raw counts across all
      // export pages produces a different (unrounded) result.
      const exportTotalRow = first.data.totals
        ? createLocationSummaryGrandTotalRow(first.data.totals)
        : computeLocationSummaryTotalRow(exportedRows);

      const exportFileName = `OnTimeDispatch_LocationSummary_${new Date().toISOString().slice(0, 10)}.xlsx`;

      const workbook = buildOnTimeLocationSummaryWorkbook({
        dimensionKeys: appliedLocationDetailDimensions,
        dimensionLabels: appliedLocationDetailDimensions.map(
          (k) => locationDetailDimensionLabelMap[k]
        ),
        totalRow: exportTotalRow,
        rows: sortedExportedRows,
      });

      await downloadWorkbook(workbook, exportFileName);

      showToast({
        header: "Export started",
        subtext: exportFileName,
        variant: "success",
        duration: 3000,
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to export location summary data";
      console.error("[LocationSummary Export] Export failed:", errorMessage);
      showToast({
        header: "Export Failed",
        subtext: errorMessage,
        variant: "error",
        duration: 5000,
      });
    } finally {
      setIsLocSummaryExporting(false);
    }
  }, [
    isLocSummaryExporting,
    locationSummaryFilters,
    adminLocations,
    locationSummaryGrainColumns,
    locSummarySortKey,
    locSummarySortDir,
    appliedFilters,
    currentYear,
    appliedLocationDetailDimensions,
    locationDetailDimensionLabelMap,
    showToast,
  ]);

  const visibleColumns = LOCATION_TABLE_COLUMNS.filter((col) =>
    locationTableColumnConfig[col.key as keyof LocationTableColumnConfig]
  );
  const metricColumnCount = 8;
  const contentWidth = Math.max(980, width - 72);

  // Fixed natural widths — columns never shrink below these regardless of column count.
  const dimensionCellWidth = 160; // region / marketPod / location / locationType
  const METRIC_COLUMN_MIN_WIDTH = 130;

  const columnWidthMap: Record<string, number> = {
    region: dimensionCellWidth,
    marketPod: dimensionCellWidth,
    location: dimensionCellWidth,
    locationType: dimensionCellWidth,
    year: 80,
    period: 80,
    weekInYear: 80,
    deliveryDate: 110,
  };

  // Only stretch metric columns to fill leftover space when the table is narrower than the
  // card; once natural widths exceed contentWidth, the outer horizontal ScrollView takes over.
  const dimensionColumnsTotalWidth = visibleColumns.reduce(
    (sum, column) => sum + (columnWidthMap[column.key] ?? dimensionCellWidth),
    0
  );
  const naturalMetricsWidth = METRIC_COLUMN_MIN_WIDTH * metricColumnCount;
  const leftoverWidth =
    contentWidth - 24 - dimensionColumnsTotalWidth - naturalMetricsWidth;
  const metricCellWidth =
    leftoverWidth > 0
      ? METRIC_COLUMN_MIN_WIDTH + Math.floor(leftoverWidth / metricColumnCount)
      : METRIC_COLUMN_MIN_WIDTH;

  const renderLocationSummarySortHeader = (
    label: string,
    key: LocSummarySortKey,
    isDimension = false
  ) => {
    const isSorted = locSummarySortKey === key;
    return (
      <View
        style={[
          styles.locationSummarySortHeaderContent,
          isDimension && styles.locationSummarySortHeaderContentDimension,
        ]}
      >
        <Text
          style={[
            styles.locationSummaryHeaderCell,
            isDark && { color: '#9A9A9A' },
            isDimension && styles.locationSummaryDimensionCell,
            isSorted && styles.locationSummaryHeaderCellActive,
            isSorted && isDark && { color: '#3D8BFF' },
          ]}
        >
          {label}
        </Text>
        <MaterialIcons
          name={
            isSorted
              ? locSummarySortDir === "asc"
                ? "keyboard-arrow-up"
                : "keyboard-arrow-down"
              : "unfold-more"
          }
          size={14}
          color={isSorted ? (isDark ? '#3D8BFF' : '#0B5FC6') : (isDark ? '#6B7280' : '#98A2B3')}
          style={styles.locationSummarySortIcon}
        />
      </View>
    );
  };

  // Shared content width so the header, Grand Total, and body ScrollViews all measure identically —
  // required for handleTableHorizontalScroll to keep their horizontal offsets perfectly aligned.
  const tableContentWidthStyle = {
    minWidth: dimensionColumnsTotalWidth + metricCellWidth * metricColumnCount + 24,
  };

  // Recompute the capped body height whenever the viewport resizes, the footer's measured height
  // changes, or the table's row/column content changes (either of which can shift the body's top
  // offset on screen).
  useEffect(() => {
    recomputeTableBodyHeight();
  }, [
    recomputeTableBodyHeight,
    locationSummaryLoading,
    visibleColumns.length,
    locationSummaryBodyRows.length,
  ]);

  // Renders a single table row (used for both the Grand Total row and every body row) so the two
  // never drift out of sync visually — column widths, cell formatting, and navigation links are
  // defined exactly once here.
  const renderLocationSummaryTableRow = (row: LocationSummaryRow, contentRowIndex: number) => {
    const isGrandTotal = !!row.isGrandTotal;
    const completedTone =
      row.completedPct >= 99.5
        ? styles.locationSummaryPositive
        : styles.locationSummaryNegative;
    const onTimeTone =
      row.onTimePct >= 98.5
        ? styles.locationSummaryPositive
        : styles.locationSummaryNegative;
    const daeToneCell =
      row.daePct >= 85
        ? styles.locationSummaryPositive
        : styles.locationSummaryNegative;

    return (
      <View
        key={row.id ?? `row-${contentRowIndex}`}
        style={[
          styles.locationSummaryTableBodyRow,
          isGrandTotal && styles.locationSummaryTotalRow,
          isDark &&
            (isGrandTotal
              ? { backgroundColor: '#1B2A3D', borderBottomColor: '#2A4070' }
              : { backgroundColor: '#1E1E1E', borderBottomColor: '#303030' }),
          !isGrandTotal &&
            contentRowIndex % 2 === 1 &&
            styles.locationSummaryTableBodyRowAlt,
          !isGrandTotal &&
            contentRowIndex % 2 === 1 &&
            isDark && { backgroundColor: '#242424' },
        ]}
      >
        {visibleColumns.map((column, colIndex) => {
          const cellWidth = columnWidthMap[column.key] ?? dimensionCellWidth;
          const cellKey = `${row.id ?? `row-${contentRowIndex}`}-${column.dimensionKey}`;

          // Hierarchical grouping: a parent value is shown only on the first row of its
          // group (its dimension path, up to this column, differs from the previous row)
          // and its cell is closed off on the last row.
          const isHierarchyRow =
            !isGrandTotal &&
            locationSummaryHierarchyMode !== "flat" &&
            !!row.groupType;
          // ASN Gap parity: in rollup modes each level reads its own dimension for
          // this column (see hierarchySourceDimension).
          const sourceDimensionKey = (
            isHierarchyRow
              ? hierarchySourceDimension(
                  row,
                  column.dimensionKey,
                  locationSummaryHierarchyMode
                )
              : column.dimensionKey
          ) as LocationDetailDimensionKey | "";
          const showChevron =
            isHierarchyRow &&
            row.groupType !== "location" &&
            (locationSummaryHierarchyMode === "region"
              ? colIndex === 0
              : row.groupType === "market" && colIndex === 1);
          const prevRow =
            !isGrandTotal && !isHierarchyRow && contentRowIndex > 0
              ? locationSummaryBodyRows[contentRowIndex - 1]
              : undefined;
          const nextRow =
            !isGrandTotal && !isHierarchyRow
              ? locationSummaryBodyRows[contentRowIndex + 1]
              : undefined;
          const pathDiffers = (other?: LocationSummaryRow) =>
            !other ||
            visibleColumns
              .slice(0, colIndex + 1)
              .some(
                (c) =>
                  (row.dimensions[c.dimensionKey] || "—") !==
                  (other.dimensions[c.dimensionKey] || "—")
              );
          const isLastDimensionColumn = colIndex === visibleColumns.length - 1;
          const isGroupStart = isGrandTotal || pathDiffers(prevRow);
          const isGroupEnd = isGrandTotal || pathDiffers(nextRow);
          const isGroupedParent =
            !isGrandTotal && !isHierarchyRow && !isLastDimensionColumn;

          const cellValue = isGrandTotal
            ? colIndex === 0
              ? "Grand Total"
              : ""
            : isHierarchyRow
              ? sourceDimensionKey
                ? row.dimensions[sourceDimensionKey] || ""
                : ""
              : isGroupStart
                ? row.dimensions[column.dimensionKey] || "—"
                : "";
          const isLinkableDimension =
            !isGrandTotal &&
            (sourceDimensionKey === "region" ||
              sourceDimensionKey === "market" ||
              sourceDimensionKey === "location");

          const cellContainerStyle = [
            { width: cellWidth },
            // ASN Gap parity: right-side divider on every dimension column except Region.
            column.dimensionKey !== "region" &&
              styles.locationSummaryColumnDivider,
            isHierarchyRow &&
              (row.groupType === "market" || row.groupType === "location") &&
              styles.locationSummaryHierarchyIndent,
            isGroupedParent && styles.locationSummaryGroupCell,
            // First tinted cell bleeds into the row's left padding so no white strip /
            // row line shows to its left.
            isGroupedParent &&
              colIndex === 0 && {
                width: cellWidth + 12,
                marginLeft: -12,
                paddingLeft: 12,
              },
            isGroupedParent && isGroupEnd && styles.locationSummaryGroupCellEnd,
            isGroupedParent &&
              isDark && {
                backgroundColor: '#222B38',
                borderBottomColor: '#303030',
              },
          ];

          // Left padding added by the divider / indent styles — taken off the
          // fixed-width Text so the content stays inside the column.
          const leftPad =
            isHierarchyRow &&
            (row.groupType === "market" || row.groupType === "location")
              ? 28
              : column.dimensionKey !== "region"
                ? 8
                : 0;
          const textWidth = showChevron ? undefined : cellWidth - leftPad;
          const chevron = showChevron ? (
            <TouchableOpacity
              onPress={() => handleToggleGroup(row.id)}
              activeOpacity={0.7}
              accessibilityLabel={
                expandedGroups.has(row.id ?? "") ? "Collapse group" : "Expand group"
              }
            >
              <MaterialIcons
                name={expandedGroups.has(row.id ?? "") ? "expand-more" : "chevron-right"}
                size={14}
                color="#828282"
                style={{ marginRight: 4 }}
              />
            </TouchableOpacity>
          ) : null;

          if (
            isLinkableDimension &&
            onNavigateToExecutiveOverview &&
            cellValue !== "" &&
            cellValue !== "—"
          ) {
            return (
              <View
                key={cellKey}
                style={[
                  cellContainerStyle,
                  showChevron && { flexDirection: "row", alignItems: "center" },
                ]}
              >
                {chevron}
                <TouchableOpacity
                  style={showChevron ? { flex: 1 } : { width: textWidth }}
                  onPress={() =>
                    handleLocationSummaryEntityPress(
                      sourceDimensionKey as "region" | "market" | "location",
                      row
                    )
                  }
                  activeOpacity={0.7}
                >
                  <Text
                    style={[
                      styles.locationSummaryBodyCell,
                      styles.locationSummaryDimensionCell,
                      styles.locationSummaryNameCell,
                      styles.locationSummaryLinkText,
                      isDark && { color: '#3D8BFF' },
                      { width: textWidth },
                    ]}
                    numberOfLines={1}
                  >
                    {cellValue}
                  </Text>
                </TouchableOpacity>
              </View>
            );
          }

          return (
            <View
              key={cellKey}
              style={[
                cellContainerStyle,
                showChevron && { flexDirection: "row", alignItems: "center" },
              ]}
            >
              {chevron}
              <Text
                style={[
                  styles.locationSummaryBodyCell,
                  styles.locationSummaryDimensionCell,
                  styles.locationSummaryNameCell,
                  isGrandTotal && styles.locationSummaryTotalCell,
                  isDark && {
                    color: isGrandTotal ? '#3D8BFF' : '#D0D0D0',
                  },
                  { width: textWidth },
                ]}
              >
                {cellValue}
              </Text>
            </View>
          );
        })}
        <Text
          style={[
            styles.locationSummaryBodyCell,
            isGrandTotal && styles.locationSummaryTotalCell,
            isDark && {
              color: isGrandTotal ? '#3D8BFF' : '#D0D0D0',
            },
            { width: metricCellWidth },
          ]}
        >
          {fmtCount(row.planned)}
        </Text>
        <Text
          style={[
            styles.locationSummaryBodyCell,
            isGrandTotal && styles.locationSummaryTotalCell,
            isDark && {
              color: isGrandTotal ? '#3D8BFF' : '#D0D0D0',
            },
            { width: metricCellWidth },
          ]}
        >
          {fmtCount(row.completed)}
        </Text>
        <Text
          style={[
            styles.locationSummaryBodyCell,
            isGrandTotal
              ? styles.locationSummaryTotalCell
              : completedTone,
            isGrandTotal && isDark && { color: '#3D8BFF' },
            { width: metricCellWidth },
          ]}
        >
          {row.completedPct.toFixed(2)}%
        </Text>
        {!isGrandTotal &&
        row.incomplete > 0 &&
        onNavigateToLoadExecutionIncomplete ? (
          <TouchableOpacity
            style={{ width: metricCellWidth }}
            onPress={() => handleLocationSummaryIncompletePress(row)}
            activeOpacity={0.7}
          >
            <Text
              style={[
                styles.locationSummaryBodyCell,
                styles.locationSummaryLinkText,
                isDark && { color: '#3D8BFF' },
                { width: metricCellWidth },
              ]}
            >
              {fmtCount(row.incomplete)}
            </Text>
          </TouchableOpacity>
        ) : (
          <Text
            style={[
              styles.locationSummaryBodyCell,
              isGrandTotal && styles.locationSummaryTotalCell,
              isDark && {
                color: isGrandTotal ? '#3D8BFF' : '#D0D0D0',
              },
              { width: metricCellWidth },
            ]}
          >
            {fmtCount(row.incomplete)}
          </Text>
        )}
        <Text
          style={[
            styles.locationSummaryBodyCell,
            isGrandTotal && styles.locationSummaryTotalCell,
            isDark && {
              color: isGrandTotal ? '#3D8BFF' : '#D0D0D0',
            },
            { width: metricCellWidth },
          ]}
        >
          {fmtCount(row.onTime)}
        </Text>
        <Text
          style={[
            styles.locationSummaryBodyCell,
            isGrandTotal
              ? styles.locationSummaryTotalCell
              : onTimeTone,
            isGrandTotal && isDark && { color: '#3D8BFF' },
            { width: metricCellWidth },
          ]}
        >
          {row.onTimePct.toFixed(2)}%
        </Text>
        <Text
          style={[
            styles.locationSummaryBodyCell,
            isGrandTotal && styles.locationSummaryTotalCell,
            isDark && {
              color: isGrandTotal ? '#3D8BFF' : '#D0D0D0',
            },
            { width: metricCellWidth },
          ]}
        >
          {fmtCount(row.daeN)}
        </Text>
        <Text
          style={[
            styles.locationSummaryBodyCell,
            isGrandTotal
              ? styles.locationSummaryTotalCell
              : daeToneCell,
            isGrandTotal && isDark && { color: '#3D8BFF' },
            { width: metricCellWidth },
          ]}
        >
          {row.daePct.toFixed(2)}%
        </Text>
      </View>
    );
  };

  return (
    <View>
      {/* ✅ Mobile view for phones (iOS with width < 768) */}
      {isMobilePhone ? (
        <LocationSummaryMobileCards
          rows={locationSummaryApiData.map((record) => {
            const selectedYearLabel =
              appliedFilters.selectedYears[0]?.displayText || String(currentYear);
            const selectedPeriodLabel =
              appliedFilters.selectedPeriods[0]?.displayText || "P6";
            const selectedWeekLabel =
              appliedFilters.selectedWeeks[0]?.displayText?.split(" ")[0] || "W2";
            const selectedDeliveryDate =
              appliedFilters.deliveryDateRange.end ||
              appliedFilters.deliveryDateRange.start ||
              toISODate(new Date());
            return mapLocationSummaryRecord(record, adminLocations, {
              selectedYearLabel,
              selectedPeriodLabel,
              selectedWeekLabel,
              selectedDeliveryDate,
            });
          })}
          loading={locationSummaryLoading}
          error={locationSummaryError?.message || null}
          adminLocations={adminLocations}
          isDark={isDarkPhone}
          onEntityPress={onNavigateToExecutiveOverview}
          onIncompleteLoadsPress={onNavigateToLoadExecutionIncomplete}
        />
      ) : (
        /* Desktop/Tablet view */
        <View style={styles.locationSummaryWrap}>
      {/* New Location Table Columns Configuration */}
      <View ref={locationCardRef} style={[styles.locationSummaryTableCard, { overflow: "visible" }, isDark && { backgroundColor: '#1E1E1E', borderColor: '#363636' }]}>
        <View style={styles.locationSummaryTableHeadRow}>
          <View style={styles.locationSummaryTableHead}>
            <View style={styles.locationSummaryTitleInline}>
              <Text style={[styles.locationSummaryTableTitle, isDark && { color: '#F2F2F2' }]}>
                Location Details
              </Text>
              {locationSummaryTableSubtitle ? (
                <Text style={[styles.locationSummaryTableByLabel, isDark && { color: '#9A9A9A' }]}>
                  {locationSummaryTableSubtitle}
                </Text>
              ) : null}
            </View>
            <Text style={[styles.locationSummaryTableSubtitle, isDark && { color: '#9A9A9A' }]}>
              Choose dimensions to display and configure table columns.
            </Text>
          </View>
          <View style={styles.locationSummaryTableActionsRow}>
            {/* Columns button — styled to match CustomerSkuUtilizationTab */}
            <TouchableOpacity
              ref={locationColumnsBtnRef}
              style={[styles.columnsBtn, isDark && { backgroundColor: '#252525', borderColor: '#3D8BFF' }, locationSummaryLoading && styles.columnsBtnDisabled]}
              onPress={openLocationColumnsDropdown}
              disabled={locationSummaryLoading}
              activeOpacity={0.8}
            >
              <MaterialIcons name="view-column" size={16} color={isDark ? '#3D8BFF' : '#0B5FC6'} />
              <Text style={[styles.columnsBtnText, isDark && { color: '#3D8BFF' }]}>
                {`Columns (${Object.values(locationTableColumnConfig).filter(Boolean).length})`}
              </Text>
            </TouchableOpacity>

            {Platform.OS === 'web' && (
              <TouchableOpacity
                style={[
                  styles.exportBtn,
                  isDark && { backgroundColor: '#252525', borderColor: '#3D8BFF' },
                  (locationSummaryLoading || isLocSummaryExporting) && styles.exportBtnDisabled,
                ]}
                onPress={handleLocSummaryExport}
                disabled={locationSummaryLoading || isLocSummaryExporting}
              >
                <MaterialIcons
                  name="download"
                  size={16}
                  color={isLocSummaryExporting ? '#6B7280' : (isDark ? '#3D8BFF' : '#0B5FC6')}
                />
                <Text
                  style={[
                    styles.exportBtnText,
                    isDark && { color: '#3D8BFF' },
                    isLocSummaryExporting && { color: '#6B7280' },
                  ]}
                >
                  {isLocSummaryExporting ? "Exporting..." : "Export Data"}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Popover — Table Columns panel, anchored to card via measureLayout */}
        {isLocationColumnsPanelOpen && (
          <>
            {/* Full-screen backdrop to dismiss on outside tap */}
            <Pressable
              style={styles.columnsPanelBackdrop}
              onPress={() => setIsLocationColumnsPanelOpen(false)}
            />
            <View
              style={[
                styles.tableColumnsPanelFloating,
                { top: locationBtnLayout.top },
                isDark && { backgroundColor: '#252525', borderColor: '#404040' },
              ]}
            >
              <Text style={[styles.tableColumnsPanelTitle, isDark && { color: '#F2F2F2' }]}>Table columns</Text>
              <Text style={[styles.tableColumnsPanelSubtitle, isDark && { color: '#9A9A9A' }]}>
                Choose the dimensions the table groups by.
              </Text>

              {/* Action Links Row: Select All | Deselect All     Expand All | Collapse All */}
              <View style={styles.tableColumnsPanelActionsRow}>
                <View style={styles.tableColumnsPanelActionsGroup}>
                  <TouchableOpacity onPress={handleLocationColumnSelectAll} activeOpacity={0.7}>
                    <Text style={[styles.tableColumnsPanelActionLink, isDark && { color: '#3D8BFF' }]}>Select All</Text>
                  </TouchableOpacity>
                  <Text style={[styles.tableColumnsPanelActionPipe, isDark && { color: '#6B7280' }]}>|</Text>
                  <TouchableOpacity onPress={handleLocationColumnDeselectAll} activeOpacity={0.7}>
                    <Text style={[styles.tableColumnsPanelActionLink, isDark && { color: '#3D8BFF' }]}>Deselect All</Text>
                  </TouchableOpacity>
                </View>

                <View style={styles.tableColumnsPanelActionsGroup}>
                  <TouchableOpacity onPress={handleLocationColumnExpandAll} activeOpacity={0.7}>
                    <Text style={[styles.tableColumnsPanelActionLink, isDark && { color: '#3D8BFF' }]}>Expand All</Text>
                  </TouchableOpacity>
                  <Text style={[styles.tableColumnsPanelActionPipe, isDark && { color: '#6B7280' }]}>|</Text>
                  <TouchableOpacity onPress={handleLocationColumnCollapseAll} activeOpacity={0.7}>
                    <Text style={[styles.tableColumnsPanelActionLink, isDark && { color: '#3D8BFF' }]}>Collapse All</Text>
                  </TouchableOpacity>
                </View>
              </View>

              {/* Geography Group */}
              <View style={[styles.tableColumnSection, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                <TouchableOpacity
                  style={[styles.tableColumnSectionHeader, isDark && { backgroundColor: '#1B2A3D' }]}
                  onPress={() =>
                    handleLocationColumnSectionToggle("geographyExpanded")
                  }
                  activeOpacity={0.8}
                >
                  <Text style={[styles.tableColumnSectionTitle, isDark && { color: '#3D8BFF' }]}>GEOGRAPHY</Text>
                  <MaterialIcons
                    name={
                      locationTableColumnSectionState.geographyExpanded
                        ? "expand-less"
                        : "chevron-right"
                    }
                    size={20}
                    color={isDark ? '#3D8BFF' : '#005CBC'}
                  />
                </TouchableOpacity>
                {locationTableColumnSectionState.geographyExpanded && (
                  <View style={[styles.tableColumnSectionContent, isDark && { backgroundColor: '#202020' }]}>
                    {LOCATION_TABLE_COLUMNS.filter(
                      (col) => col.group === "Geography"
                    ).map((column) => {
                      const isChecked = !!draftLocationColumnConfig[
                        column.key as keyof LocationTableColumnConfig
                      ];
                      return (
                        <TouchableOpacity
                          key={column.key}
                          style={styles.tableColumnCheckboxRow}
                          onPress={() =>
                            handleLocationColumnToggle(
                              column.key as keyof LocationTableColumnConfig
                            )
                          }
                          activeOpacity={0.75}
                        >
                          <MaterialIcons
                            name={isChecked ? "check-box" : "check-box-outline-blank"}
                            size={18}
                            color={isChecked ? (isDark ? '#3D8BFF' : '#005CBC') : '#6B7280'}
                          />
                          <Text style={[styles.tableColumnCheckboxLabel, isDark && { color: '#D0D0D0' }]}>
                            {column.label}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                )}
              </View>

              {/* Time Group */}
              <View style={[styles.tableColumnSection, isDark && { backgroundColor: '#202020', borderColor: '#404040' }]}>
                <TouchableOpacity
                  style={[styles.tableColumnSectionHeader, isDark && { backgroundColor: '#1B2A3D' }]}
                  onPress={() =>
                    handleLocationColumnSectionToggle("timeframeExpanded")
                  }
                  activeOpacity={0.8}
                >
                  <Text style={[styles.tableColumnSectionTitle, isDark && { color: '#3D8BFF' }]}>TIME PERIOD</Text>
                  <MaterialIcons
                    name={
                      locationTableColumnSectionState.timeframeExpanded
                        ? "expand-less"
                        : "chevron-right"
                    }
                    size={20}
                    color={isDark ? '#3D8BFF' : '#005CBC'}
                  />
                </TouchableOpacity>
                {locationTableColumnSectionState.timeframeExpanded && (
                  <View style={[styles.tableColumnSectionContent, isDark && { backgroundColor: '#202020' }]}>
                    {LOCATION_TABLE_COLUMNS.filter(
                      (col) => col.group === "Time"
                    ).map((column) => {
                      const isChecked = !!draftLocationColumnConfig[
                        column.key as keyof LocationTableColumnConfig
                      ];
                      return (
                        <TouchableOpacity
                          key={column.key}
                          style={styles.tableColumnCheckboxRow}
                          onPress={() =>
                            handleLocationColumnToggle(
                              column.key as keyof LocationTableColumnConfig
                            )
                          }
                          activeOpacity={0.75}
                        >
                          <MaterialIcons
                            name={isChecked ? "check-box" : "check-box-outline-blank"}
                            size={18}
                            color={isChecked ? (isDark ? '#3D8BFF' : '#005CBC') : '#6B7280'}
                          />
                          <Text style={[styles.tableColumnCheckboxLabel, isDark && { color: '#D0D0D0' }]}>
                            {column.label}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                )}
              </View>

              <TouchableOpacity
                style={[
                  styles.tableColumnsApplyBtn,
                  Object.values(draftLocationColumnConfig).every((v) => !v) && styles.columnsBtnDisabled,
                ]}
                onPress={handleApplyLocationColumns}
                disabled={Object.values(draftLocationColumnConfig).every((v) => !v)}
                activeOpacity={0.8}
                accessibilityLabel="Apply table columns"
              >
                <Text style={styles.tableColumnsApplyBtnText}>Apply</Text>
              </TouchableOpacity>
            </View>
          </>
        )}

        {/* Table Rendering or Loading Skeleton */}
        {locationSummaryLoading ? (
          <TableSkeleton rows={Math.min(locSummaryPageSize, 25)} isDark={isDark} />
        ) : visibleColumns.length === 0 ? (
          /* Empty state when no grain columns are selected */
          <View style={[styles.locationSummaryEmptyStateContainer, { backgroundColor: colors.background.surface }]}>
            <Text style={[styles.locationSummaryEmptyStateMessage, { color: colors.text.secondary }]}>
              Select at least one grain column to display data.
            </Text>
          </View>
        ) : (
          <>
            {/* Header row — horizontal scroll only. Rendered above the capped, vertically-
                scrollable body (below) so it stays visible while the user scrolls through data. */}
            <ScrollView
              ref={headerScrollRef}
              horizontal
              showsHorizontalScrollIndicator={false}
              style={Platform.OS === 'web' ? ({ overflowX: 'auto' } as any) : undefined}
              scrollEventThrottle={16}
              onScroll={(event) =>
                handleTableHorizontalScroll('header', event.nativeEvent.contentOffset.x)
              }
            >
              <View style={[styles.locationSummaryTableWrap, tableContentWidthStyle]}>
                <View style={[styles.locationSummaryTableHeaderRow, isDark && { backgroundColor: '#252525', borderColor: '#363636' }]}>
                  {visibleColumns.map((column) => {
                    const dimensionKey = column.dimensionKey;
                    return (
                      <TouchableOpacity
                        key={`head-${dimensionKey}`}
                        style={[
                          { width: columnWidthMap[column.key] ?? dimensionCellWidth },
                          dimensionKey !== "region" && styles.locationSummaryColumnDivider,
                        ]}
                        onPress={() =>
                          handleLocSummarySort(dimensionKey as LocSummarySortKey)
                        }
                      >
                        {renderLocationSummarySortHeader(
                          column.label.toUpperCase(),
                          dimensionKey as LocSummarySortKey,
                          true
                        )}
                      </TouchableOpacity>
                    );
                  })}
                  {(
                    [
                      { key: "planned", label: "PLANNED\nLOADS" },
                      { key: "completed", label: "COMPLETED\nLOADS" },
                      { key: "completedPct", label: "COMPLETED %" },
                      { key: "incomplete", label: "INCOMPLETE\nLOADS" },
                      { key: "onTime", label: "ON-TIME\nLOADS" },
                      { key: "onTimePct", label: "ON-TIME %" },
                      { key: "daeN", label: "DRIVER ARRIVED\nAS EXPECTED" },
                      { key: "daePct", label: "ARRIVED AS\nEXPECTED %" },
                    ] as { key: LocSummarySortKey; label: string }[]
                  ).map(({ key, label }) => (
                    <TouchableOpacity
                      key={`head-${key}`}
                      style={{ width: metricCellWidth }}
                      onPress={() => handleLocSummarySort(key)}
                    >
                      {renderLocationSummarySortHeader(
                        label.replace("\\n", "\n"),
                        key
                      )}
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            </ScrollView>

            {/* Grand Total row — rendered as its own fixed row (outside the capped, scrollable
                body below) so it remains visible above the scrolling data, just like the header.
                Its horizontal position is kept in sync with the header/body by
                handleTableHorizontalScroll; it never scrolls on its own (scrollEnabled={false}) —
                only ever moved programmatically alongside the header/body. */}
            {locationSummaryGrandTotalRow && (
              <ScrollView
                ref={grandTotalScrollRef}
                horizontal
                showsHorizontalScrollIndicator={false}
                scrollEnabled={false}
              >
                <View style={[styles.locationSummaryTableWrap, tableContentWidthStyle]}>
                  {renderLocationSummaryTableRow(locationSummaryGrandTotalRow, 0)}
                </View>
              </ScrollView>
            )}

            {/* Body — capped to the available viewport height (see recomputeTableBodyHeight) so
                the table never grows taller than the screen. Vertical scrolling happens only
                inside this container; horizontal scroll stays in sync with the header/Grand Total
                via handleTableHorizontalScroll. */}
            <View ref={tableBodyAnchorRef} onLayout={recomputeTableBodyHeight}>
              <ScrollView
                style={{ maxHeight: tableBodyMaxHeight ?? undefined }}
                nestedScrollEnabled
              >
                <ScrollView
                  ref={bodyScrollRef}
                  horizontal
                  showsHorizontalScrollIndicator={Platform.OS === 'web'}
                  style={Platform.OS === 'web' ? ({ overflowX: 'auto' } as any) : undefined}
                  scrollEventThrottle={16}
                  onScroll={(event) =>
                    handleTableHorizontalScroll('body', event.nativeEvent.contentOffset.x)
                  }
                >
                  <View style={[styles.locationSummaryTableWrap, tableContentWidthStyle]}>
                    {locationSummaryBodyRows.map((row, rowIndex) =>
                      renderLocationSummaryTableRow(row, rowIndex)
                    )}

                    {locationSummaryBodyRows.length === 0 && (
                      <View style={styles.locationSummaryEmptyRow}>
                        <Text style={[styles.locationSummaryEmptyText, isDark && { color: '#9A9A9A' }]}>
                          No records match the current filters
                        </Text>
                      </View>
                    )}
                  </View>
                </ScrollView>
              </ScrollView>
            </View>

            {/* Footer / pagination — sits below the capped body; its measured height feeds back
                into recomputeTableBodyHeight so the body's cap accounts for the exact space the
                footer occupies. */}
            <View
              style={styles.locationSummaryFooterRow}
              onLayout={(event) => setTableFooterHeight(event.nativeEvent.layout.height)}
            >
              <Text style={[styles.locationSummaryFooterText, isDark && { color: '#9A9A9A' }]}>
                {`Showing ${
                  locationSummaryPaginationTotalElements === 0
                    ? 0
                    : Math.min(
                        (locSummaryPage - 1) * locSummaryPageSize + 1,
                        locationSummaryPaginationTotalElements
                      )
                }-${Math.min(
                  locSummaryPage * locSummaryPageSize,
                  locationSummaryPaginationTotalElements
                )} of ${locationSummaryPaginationTotalElements} rows`}
              </Text>
              <View style={styles.locationSummaryPaginationControls}>
                <TouchableOpacity
                  style={[
                    styles.locationSummaryPaginationButton,
                    isDark && { backgroundColor: '#252525', borderColor: '#404040' },
                    locSummaryPage <= 1 &&
                      styles.locationSummaryPaginationButtonDisabled,
                    locSummaryPage <= 1 && isDark && { backgroundColor: '#1E1E1E', borderColor: '#303030' },
                  ]}
                  onPress={() => handleLocSummaryPageChange(locSummaryPage - 1)}
                  disabled={locSummaryPage <= 1}
                  activeOpacity={0.7}
                  accessibilityLabel="Previous page"
                >
                  <MaterialIcons
                    name="chevron-left"
                    size={20}
                    color={locSummaryPage <= 1 ? (isDark ? '#4B4B4B' : '#CED4DA') : (isDark ? '#3D8BFF' : '#0B5FC6')}
                  />
                </TouchableOpacity>
                <Text style={[styles.locationSummaryPaginationPageText, isDark && { color: '#D0D0D0' }]}>
                  {`Page ${locSummaryPage} of ${locationSummaryPaginationTotalPages}`}
                </Text>
                <TouchableOpacity
                  style={[
                    styles.locationSummaryPaginationButton,
                    isDark && { backgroundColor: '#252525', borderColor: '#404040' },
                    locSummaryPage >= locationSummaryPaginationTotalPages &&
                      styles.locationSummaryPaginationButtonDisabled,
                    locSummaryPage >= locationSummaryPaginationTotalPages && isDark && { backgroundColor: '#1E1E1E', borderColor: '#303030' },
                  ]}
                  onPress={() => handleLocSummaryPageChange(locSummaryPage + 1)}
                  disabled={locSummaryPage >= locationSummaryPaginationTotalPages}
                  activeOpacity={0.7}
                  accessibilityLabel="Next page"
                >
                  <MaterialIcons
                    name="chevron-right"
                    size={20}
                    color={
                      locSummaryPage >= locationSummaryPaginationTotalPages
                        ? (isDark ? '#4B4B4B' : '#CED4DA')
                        : (isDark ? '#3D8BFF' : '#0B5FC6')
                    }
                  />
                </TouchableOpacity>
              </View>
              <View style={styles.locationSummaryPageSizeWrap}>
                {([25, 50, 100, 150, 200] as const).map((size) => (
                  <TouchableOpacity
                    key={size}
                    onPress={() => handleLocSummaryPageSizeChange(size)}
                    style={[
                      styles.locationSummaryPageSizePill,
                      isDark && { backgroundColor: '#252525', borderColor: '#404040' },
                      locSummaryPageSize === size && styles.locationSummaryPageSizePillActive,
                      locSummaryPageSize === size && isDark && { backgroundColor: '#1B2A3D', borderColor: '#3D8BFF' },
                    ]}
                  >
                    <Text
                      style={[
                        styles.locationSummaryPageSizeText,
                        isDark && { color: '#9A9A9A' },
                        locSummaryPageSize === size &&
                          styles.locationSummaryPageSizeTextActive,
                        locSummaryPageSize === size && isDark && { color: '#3D8BFF' },
                      ]}
                    >
                      {size}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </>
        )}

      </View>
      </View>
      )}
    </View>
  );
};

export default LocationSummaryTab;

const styles = StyleSheet.create({
  locationSummaryWrap: {
    gap: 14,
  },
  locationDetailsConfigCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    paddingHorizontal: 18,
    paddingTop: 12,
    paddingBottom: 16,
    shadowColor: "#A8B4C5",
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
    zIndex: 20,
    position: "relative",
    overflow: "visible",
  },
  locationDetailsHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  locationDetailsTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexShrink: 1,
  },
  locationDetailsTitle: {
    fontSize: 14,
    fontFamily: FontFamily.bold,
    color: "#2D3748",
  },
  locationDetailsPill: {
    backgroundColor: "#0B5FC6",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  locationDetailsPillText: {
    color: "#FFFFFF",
    fontSize: 11,
    fontFamily: FontFamily.bold,
  },
  locationDetailsHint: {
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: "#6B7280",
    flexShrink: 1,
  },
  locationDetailsSubtitle: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: "#6B7280",
    marginBottom: 10,
  },
  locationDetailsColumnsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "flex-start",
    overflow: "visible",
  },
  locationDetailsColumnCard: {
    width: 200,
    borderRadius: 6,
    padding: 0,
    backgroundColor: "#FFFFFF",
    minHeight: 72,
    zIndex: 2,
    overflow: "visible",
  },
  locationDetailsColumnCardOpen: {
    zIndex: 160,
    elevation: 12,
  },
  locationDetailsColumnHead: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  locationDetailsColumnLabel: {
    fontSize: 11,
    fontFamily: FontFamily.bold,
    color: "#6A6D71",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  locationDetailsRemoveButton: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 1,
    borderColor: "#D0D5DD",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
  },
  locationDetailsFixedSelector: {
    height: 36,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#0B5FC6",
    backgroundColor: "#FFFFFF",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    paddingHorizontal: 10,
  },
  locationDetailsFixedSelectorText: {
    fontSize: 13,
    color: "#0B5FC6",
    fontFamily: FontFamily.bold,
  },
  locationDetailsDropdownWrap: {
    position: "relative",
    zIndex: 30,
    overflow: "visible",
  },
  locationDetailsDropdownWrapOpen: {
    zIndex: 180,
  },
  locationDetailsSelector: {
    height: 36,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#D0D5DD",
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  locationDetailsSelectorText: {
    flex: 1,
    fontSize: 13,
    color: "#1F2937",
    fontFamily: FontFamily.medium,
  },
  locationDetailsSelectorPlaceholder: {
    color: "#9CA3AF",
  },
  locationDetailsDropdownMenu: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 40,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    shadowColor: "#000000",
    shadowOpacity: 0.08,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 6,
    zIndex: 80,
    overflow: "hidden",
  },
  locationDetailsDropdownOption: {
    paddingHorizontal: 10,
    paddingVertical: 9,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  locationDetailsDropdownOptionDisabled: {
    backgroundColor: "#F9FAFB",
  },
  locationDetailsDropdownOptionSelected: {
    backgroundColor: "#EFF5FF",
  },
  locationDetailsDropdownOptionText: {
    overflow: "visible",
    fontSize: 13,
    color: "#1F2937",
    fontFamily: FontFamily.medium,
  },
  locationDetailsDropdownOptionTextDisabled: {
    color: "#9CA3AF",
  },
  locationDetailsDropdownOptionTextSelected: {
    color: "#0B5FC6",
    fontFamily: FontFamily.bold,
  },
  locationDetailsAddColumnBtn: {
    height: 32,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: "#0B5FC6",
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    backgroundColor: "#FFFFFF",
  },
  locationDetailsAddColumnBtnDisabled: {
    borderColor: "#D1D5DB",
    backgroundColor: "#F9FAFB",
  },
  locationDetailsAddColumnText: {
    fontSize: 13,
    color: "#0B5FC6",
    fontFamily: FontFamily.bold,
  },
  locationDetailsAddColumnTextDisabled: {
    color: "#9CA3AF",
  },
  locationDetailsBottomRow: {
    marginTop: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  locationDetailsActionsRow: {
    flexDirection: "row",
    gap: 10,
  },
  locationSummaryTableHeadRow: {
    paddingHorizontal: 16,
    marginBottom: 8,
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  locationSummaryTableCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    paddingTop: 16,
    paddingBottom: 8,
    shadowColor: "#A8B4C5",
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
    zIndex: 1,
    position: "relative",
  },
  locationSummaryTableHead: {
    flex: 1,
  },
  locationSummaryTitleInline: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  locationSummaryTableByLabel: {
    fontSize: 13,
    color: "#616161",
    fontWeight: "500",
    fontFamily: FontFamily.medium,
  },
  locationSummaryTableTitle: {
    fontSize: 18,
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    color: "#111827",
  },
  locationSummaryTableSubtitle: {
    marginTop: 2,
    fontSize: 12,
    color: "#6B7280",
    fontFamily: FontFamily.regular,
  },
  exportBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderWidth: 1,
    borderColor: "#0B5FC6",
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: "#FFFFFF",
    alignSelf: "flex-start",
  },
  exportBtnText: {
    fontSize: 14,
    color: "#0B5FC6",
    fontFamily: FontFamily.bold,
    fontWeight: "700",
  },
  exportBtnDisabled: {
    opacity: 0.5,
  },
  locationSummaryTableActionsRow: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
    alignSelf: "flex-start",
  },
  columnsBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderWidth: 1,
    borderColor: "#0B5FC6",
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: "#FFFFFF",
    alignSelf: "flex-start",
  },
  columnsBtnWrap: {
    position: "relative",
    zIndex: 300,
  },
  columnsPanelBackdrop: {
    position: "absolute" as any,
    top: -9999,
    left: -9999,
    right: -9999,
    bottom: -9999,
    zIndex: 998,
  },
  columnsBtnText: {
    fontSize: 14,
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    color: "#0B5FC6",
  },
  columnsBtnDisabled: {
    opacity: 0.5,
  },
  tableColumnsApplyBtn: {
    alignSelf: "flex-end",
    marginTop: 4,
    paddingHorizontal: 22,
    paddingVertical: 8,
    borderRadius: 18,
    backgroundColor: "#0B5FC6",
  },
  tableColumnsApplyBtnText: {
    fontSize: 14,
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    color: "#FFFFFF",
  },
  tableColumnsPanelFloating: {
    position: "absolute",
    right: 0,
    zIndex: 999,
    width: 460,
    maxWidth: "92%" as any,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E3E8EF",
    padding: 18,
    ...(Platform.OS === "web"
      ? { boxShadow: "0 16px 40px rgba(16, 24, 40, 0.16)" } as any
      : {
          shadowColor: "#101828",
          shadowOpacity: 0.16,
          shadowRadius: 20,
          shadowOffset: { width: 0, height: 8 },
          elevation: 8,
        }),
  },
  tableColumnsPanelHeader: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
  tableColumnsPanelTitle: {
    fontSize: 15,
    fontFamily: FontFamily.bold,
    color: "#1F2937",
    marginBottom: 3,
  },
  tableColumnsPanelSubtitle: {
    fontSize: 12.5,
    color: "#6B7280",
    fontFamily: FontFamily.regular,
    marginBottom: 14,
  },
  tableColumnsPanelActionsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  tableColumnsPanelActionsGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  tableColumnsPanelActionLink: {
    fontSize: 12,
    color: "#0B5FC6",
    fontFamily: FontFamily.medium,
    fontWeight: "600",
  },
  tableColumnsPanelActionPipe: {
    fontSize: 12,
    color: "#9CA3AF",
    fontFamily: FontFamily.regular,
  },
  tableColumnSection: {
    borderWidth: 1,
    borderColor: "#E3E8EF",
    borderRadius: 8,
    overflow: "hidden",
    marginBottom: 10,
    backgroundColor: "#FFFFFF",
  },
  tableColumnSectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: "#EFF4FD",
  },
  tableColumnSectionTitle: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
    color: "#005CBC",
    letterSpacing: 0.3,
  },
  tableColumnSectionContent: {
    flexDirection: "row",
    flexWrap: "wrap",
    columnGap: 16,
    rowGap: 12,
    padding: 14,
  },
  tableColumnCheckboxRow: {
    width: "45%",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  tableColumnCheckbox: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  checkboxBase: {
    width: 18,
    height: 18,
    borderRadius: 3,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    backgroundColor: "#FFFFFF",
    justifyContent: "center",
    alignItems: "center",
  },
  checkboxChecked: {
    backgroundColor: "#0B5FC6",
    borderColor: "#0B5FC6",
  },
  tableColumnCheckboxLabel: {
    fontSize: 13,
    color: "#1F2937",
    fontFamily: FontFamily.regular,
  },
  locationSummaryTableWrap: {
    // minWidth is computed dynamically inline based on visible columns + metric widths
    paddingRight: 12,
  },
  locationSummaryTableHeaderRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderBottomWidth: 2,
    borderColor: "#E5E7EB",
    backgroundColor: "#F9FAFB",
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  locationSummaryHeaderCell: {
    textAlign: "right",
    fontSize: 11,
    color: "#667085",
    fontFamily: FontFamily.bold,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    lineHeight: 14,
  },
  locationSummaryHeaderCellActive: {
    color: "#0B5FC6",
  },
  locationSummaryDimensionCell: {
    textAlign: "left",
  },
  locationSummarySortHeaderContent: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  locationSummarySortHeaderContentDimension: {
    justifyContent: "flex-start",
  },
  locationSummarySortIcon: {
    marginLeft: 2,
  },
  locationSummaryTableBodyRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#EEF0F4",
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: "#FFFFFF",
  },
  locationSummaryTableBodyRowAlt: {
    backgroundColor: "#FCFDFF",
  },
  // Parent (grouped) dimension cell: light tint that stretches over the row's
  // padding + bottom border so consecutive rows of one group read as a single
  // merged cell; the separator is only drawn on the group's last row.
  locationSummaryGroupCell: {
    backgroundColor: "#F5F8FE",
    marginTop: -10,
    marginBottom: -11,
    paddingTop: 10,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#F5F8FE",
    borderRightWidth: 1,
    borderRightColor: "#E3EAF6",
  },
  locationSummaryGroupCellEnd: {
    borderBottomColor: "#EEF0F4",
  },
  // Same divider ASN Gap's Location Summary draws between dimension columns.
  locationSummaryColumnDivider: {
    borderLeftWidth: 1,
    borderLeftColor: "#EBEBEB",
    borderStyle: "solid",
    alignSelf: "stretch",
    justifyContent: "center",
    paddingLeft: 8,
  },
  // Market / Location rollup rows are indented under their parent (ASN Gap parity).
  locationSummaryHierarchyIndent: {
    paddingLeft: 28,
  },
  locationSummaryBodyCell: {
    width: 150,
    textAlign: "right",
    fontSize: 13,
    color: "#111827",
    fontFamily: FontFamily.medium,
  },
  locationSummaryNameCell: {
    fontFamily: FontFamily.bold,
  },
  locationSummaryTotalRow: {
    backgroundColor: "#F3F7FD",
    borderTopWidth: 2,
    borderTopColor: "#D7E5F8",
    borderBottomWidth: 2,
    borderBottomColor: "#D7E5F8",
    paddingVertical: 20,
    alignItems: "center",
  },
  locationSummaryTotalCell: {
    color: "#0B5FC6",
    fontSize: 15,
    fontFamily: FontFamily.bold,
    fontWeight: "700",
  },
  locationSummaryPositive: {
    color: "#0B5FC6",
    fontFamily: FontFamily.bold,
  },
  locationSummaryNegative: {
    color: "#C2410C",
    fontFamily: FontFamily.bold,
  },
  locationSummaryLinkText: {
    color: "#0B5FC6",
    fontFamily: FontFamily.bold,
  },
  locationSummaryFooterRow: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  locationSummaryFooterText: {
    fontSize: 12,
    color: "#6B7280",
    fontFamily: FontFamily.medium,
  },
  locationSummaryPaginationControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  locationSummaryPaginationButton: {
    width: 28,
    height: 28,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#DEE2E6",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
  },
  locationSummaryPaginationButtonDisabled: {
    backgroundColor: "#F8F9FA",
    borderColor: "#F0F0F0",
  },
  locationSummaryPaginationPageText: {
    fontSize: 12,
    color: "#111827",
    fontFamily: FontFamily.medium,
    minWidth: 90,
    textAlign: "center",
  },
  locationSummaryPageSizeWrap: {
    flexDirection: "row",
    gap: 6,
    alignItems: "center",
  },
  locationSummaryPageSizePill: {
    width: 30,
    height: 24,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#D0D5DD",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
  },
  locationSummaryPageSizePillActive: {
    borderColor: "#0B5FC6",
    backgroundColor: "#EFF5FF",
  },
  locationSummaryPageSizeText: {
    fontSize: 12,
    color: "#6B7280",
    fontFamily: FontFamily.bold,
  },
  locationSummaryPageSizeTextActive: {
    color: "#0B5FC6",
  },
  locationSummaryEmptyRow: {
    paddingHorizontal: 12,
    paddingVertical: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  locationSummaryEmptyText: {
    fontSize: 13,
    color: "#6B7280",
    fontFamily: FontFamily.medium,
  },
  locationSummaryEmptyStateContainer: {
    minHeight: 200,
    paddingHorizontal: 12,
    paddingVertical: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  locationSummaryEmptyStateMessage: {
    fontSize: 14,
    fontFamily: FontFamily.medium,
    textAlign: "center",
    maxWidth: "80%",
  },
});

